import { type DateFormat, Format, dateFns } from '@/lib/dateFns';
import { newDate } from '@/utils/date';
import { formatInTimeZone } from 'date-fns-tz';

/**
 * 汎用日時フォーマッター
 * @param value
 * @returns
 */
export const formatDateCommon = (value: string | Date, format: DateFormat, isJST = false) => {
  if (!value) return '';
  if (typeof value === 'string') {
    return isJST ? formatInTimeZone(newDate(value), 'Asia/Tokyo', format) : dateFns.format(newDate(value), format);
  }
  return isJST ? formatInTimeZone(value, 'Asia/Tokyo', format) : dateFns.format(value, format);
};

/**
 * 日時フォーマッター
 * @param value
 * @returns
 */
export const formatDateTime = (value: string | Date) => {
  if (!value) return '';
  if (typeof value === 'string') {
    return dateFns.format(newDate(value), Format.dateTime);
  }
  return dateFns.format(value, Format.dateTime);
};

/**
 * 数値フォーマッター（金額など）
 * @param value
 * @param blank2Zero
 * @returns
 */
export const formatCurrency = (value: string | number, blank2Zero?: boolean) => {
  if (value == null) {
    return blank2Zero ? '0' : '';
  }
  if (typeof value === 'string') {
    return Number.parseInt(value, 10).toLocaleString();
  }
  return value.toLocaleString();
};

/**
 * 住所フォーマッター
 * @param value
 * @returns
 */
export const formatPostCode = (value: string | number) => {
  if (!value) return '';

  // 数字以外の文字を除去
  const cleanZip = value.toString().replace(/[^\d]/g, '');

  // 7桁あれば3-4で分割
  if (cleanZip.length === 7) {
    return `${cleanZip.slice(0, 3)}-${cleanZip.slice(3)}`;
  }
  // 形式が合わない場合はそのまま返す
  return value;
};

/**
 * 配列の要素から指定したプロパティの値を取得し、表示用文字列に変換する共通関数
 * - 要素が複数ある場合: `/` で連結する
 * - 要素が1つの場合: そのまま返す
 * @param items オブジェクトの配列
 * @param getValue 各要素から値を取得する関数
 * @returns 表示用の文字列
 */
const formatArrayItems = <T>(items: T[] | null | undefined, getValue: (item: T) => string): string => {
  if (!items || items.length === 0) {
    return '';
  }
  if (items.length === 1) {
    return getValue(items[0]);
  }
  return items.map(getValue).join(' / ');
};

/**
 * 作家名を表示用文字列に変換する
 * - 作家が複数ある場合: `/` で連結する
 * - 作家が1つの場合: そのまま返す
 * @param creators 作家の配列（nameプロパティを持つオブジェクトの配列）
 * @returns 表示用の文字列
 */
export const formatCreatorNames = (creators: Array<{ name: string }>): string => {
  return formatArrayItems(creators, (creator) => creator.name);
};
